import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from './useAdminApi';

/** Types mirrored from netlify/functions/admin-account-closure.ts */

export interface ClosureRow {
  id: string;
  customer_id: string | null;
  qbo_customer_id?: number | null;
  status: 'in_progress' | 'closed' | 'cancelled';
  equipment_override_note: string | null;
  equipment_override_at: string | null;
  equipment_override_by: string | null;
  tanks_resolution: 'pickup_scheduled' | 'closure_audit' | null;
  tanks_note: string | null;
  tanks_resolved_at: string | null;
  tanks_resolved_by: string | null;
  balance_writeoff_note: string | null;
  balance_resolved_at: string | null;
  balance_resolved_by: string | null;
  refunds_answer: 'none' | 'return_order' | null;
  refund_note: string | null;
  refund_lines: Array<{ name: string; display_name?: string; qbo_item_id?: string; qty: number; unit_price: number }> | null;
  refund_total: number | null;
  refund_sf_job_id: string | null;
  refund_qbo_credit_memo_id: string | null;
  refund_qbo_credit_memo_doc: string | null;
  refunds_resolved_at: string | null;
  refunds_resolved_by: string | null;
  closed_at: string | null;
  closed_by: string | null;
  /** Final-step deactivation results (QuickBooks + Service Fusion). */
  qbo_deactivated_at: string | null;
  qbo_deactivate_error: string | null;
  sf_closeout_job_id: string | null;
  sf_deactivate_error: string | null;
  created_by: string | null;
  created_at: string;
}

export interface ClosureGates {
  equipment: {
    ok: boolean;
    onSiteCount: number;
    assets: Array<{
      id: string;
      asset_tag: string | null;
      catalog_name: string | null;
      qbo_item_name: string | null;
      description: string | null;
      status: string | null;
      qty: number | null;
    }>;
    overridden: boolean;
  };
  tanks: {
    ok: boolean;
    total: number;
    buckets: Array<{ label: string; systemCount: number }>;
    resolution: string | null;
    recentAudit: { id: string; doc_number: string | null; created_at: string } | null;
  };
  balances: { ok: boolean; total: number; count: number; writtenOff: boolean };
  refunds: {
    ok: boolean;
    answer: string | null;
    sfJobId: string | null;
    creditMemoId: string | null;
    creditMemoDoc: string | null;
  };
  /** Only enforced for a QuickBooks-only customer: close active stores first. */
  stores?: { ok: boolean; active: Array<{ id: string; name: string }> };
  allClear: boolean;
}

/** Portal uuid OR QuickBooks id — the closure engine takes either. */
export interface ClosureSubject {
  customerId?: string;
  qboId?: string;
}

const subjectQuery = (s: ClosureSubject) =>
  s.customerId ? `customer_id=${s.customerId}` : `qbo_customer_id=${s.qboId}`;
const subjectKey = (s: ClosureSubject) => s.customerId ?? `qbo:${s.qboId}`;

export interface ClosureState {
  ok: true;
  customer: { id: string | null; qbo_customer_id?: number; name: string; active: boolean; on_portal?: boolean };
  closure: ClosureRow | null;
  gates: ClosureGates;
}

export function useAccountClosure(subject: ClosureSubject) {
  return useQuery<ClosureState>({
    queryKey: ['admin', 'closure', subjectKey(subject)],
    queryFn: () => adminFetch(`admin-account-closure?${subjectQuery(subject)}`),
    enabled: !!(subject.customerId || subject.qboId),
    staleTime: 10 * 1000,
  });
}

export type ClosureAction =
  | { action: 'start' }
  | { action: 'cancel' }
  | { action: 'override_equipment'; note: string }
  | { action: 'resolve_tanks'; resolution: 'pickup_scheduled' | 'closure_audit'; note?: string }
  | { action: 'writeoff_balance'; note: string }
  | { action: 'resolve_refunds'; answer: 'none'; note?: string }
  | {
      action: 'return_order';
      lines: Array<{ name: string; display_name?: string; qbo_item_id: string; qty: number; unit_price: number }>;
      note?: string;
    }
  | { action: 'close' };

export function useClosureAction(subject: ClosureSubject) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ClosureAction) =>
      adminFetch<{ ok: true; sf_job_id?: string | null; closed?: boolean }>(
        'admin-account-closure',
        { method: 'POST', body: JSON.stringify({ ...(subject.customerId ? { customer_id: subject.customerId } : { qbo_customer_id: subject.qboId }), ...input }) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'closure', subjectKey(subject)] });
      if (subject.customerId) qc.invalidateQueries({ queryKey: ['admin', 'customer-detail', subject.customerId] });
      qc.invalidateQueries({ queryKey: ['admin', 'customers'] });
    },
  });
}

export interface CatalogPickerItem {
  id: string;
  name: string;
  display_name: string | null;
  sku: string | null;
  list_price: number | null;
  qbo_item_id: string;
}

export async function searchClosureCatalog(q: string): Promise<CatalogPickerItem[]> {
  const res = await adminFetch<{ ok: true; items: CatalogPickerItem[] }>(
    `admin-account-closure?catalog_search=${encodeURIComponent(q)}`,
  );
  return res.items;
}
